"use client"

import { FC, ReactNode, useState, useMemo } from "react"
import { Button } from "@/components/ui/button"
import {
  IconChevronRight,
  IconX
} from "@tabler/icons-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  BuiltInTaskStatus,
  TaskStatus
} from "@/types/spaces"
import type { StatusTemplate } from "@/types/view-templates"
import { cn } from "@/lib/utils"
import { toast } from "sonner"
import { TaskStatusIcon, resolveTaskStatusIconName } from "./task-status-icon"

interface StatusChangeButtonProps {
  currentStatus: TaskStatus
  onStatusChange: (newStatus: TaskStatus) => Promise<void>
  className?: string
  size?: "sm" | "md" | "lg"
  showLabel?: boolean
  availableStatuses?: TaskStatus[] // Allow custom status lists
  statusTemplate?: StatusTemplate | null
  showNextAction?: boolean
  triggerTestId?: string
  iconTestIdPrefix?: string
}

// Default status progression flow (fallback)
const DEFAULT_STATUS_PROGRESSION: Record<BuiltInTaskStatus, TaskStatus[]> = {
  todo: ["in_progress", "done"],
  in_progress: ["done", "blocked"],
  done: ["in_progress"], // Allow reopening
  blocked: ["todo", "in_progress"],
  cancelled: ["todo"] // Allow reopening cancelled tasks
}

const STATUS_LABELS: Record<BuiltInTaskStatus, string> = {
  todo: "To Do",
  in_progress: "In Progress", 
  done: "Done",
  blocked: "Blocked",
  cancelled: "Cancelled"
}

const STATUS_COLORS: Record<BuiltInTaskStatus, string> = {
  todo: "bg-gray-100 text-gray-800 border-gray-200",
  in_progress: "bg-blue-100 text-blue-800 border-blue-200",
  done: "bg-green-100 text-green-800 border-green-200",
  blocked: "bg-red-100 text-red-800 border-red-200",
  cancelled: "bg-gray-100 text-gray-800 border-gray-200"
}

const TEMPLATE_COLOR_CLASSES: Record<string, string> = {
  blue: "bg-blue-100 text-blue-800 border-blue-200",
  green: "bg-green-100 text-green-800 border-green-200",
  orange: "bg-orange-100 text-orange-800 border-orange-200",
  purple: "bg-purple-100 text-purple-800 border-purple-200",
  red: "bg-red-100 text-red-800 border-red-200",
  slate: "bg-slate-100 text-slate-800 border-slate-200",
  yellow: "bg-yellow-100 text-yellow-800 border-yellow-200"
}

const normalizeStatusKey = (status: string) =>
  status.toLowerCase().replace(/[-\s]/g, "_")

export const StatusChangeButton: FC<StatusChangeButtonProps> = ({
  currentStatus,
  onStatusChange,
  className,
  size = "md",
  showLabel = true,
  availableStatuses,
  statusTemplate,
  showNextAction = false,
  triggerTestId,
  iconTestIdPrefix
}) => {
  const [isChanging, setIsChanging] = useState(false)

  // Get available statuses - use custom list or fallback to all standard statuses
  const allStatuses =
    availableStatuses && availableStatuses.length > 0
      ? availableStatuses
      : (["todo", "in_progress", "done", "blocked", "cancelled"] satisfies TaskStatus[])

  const getTemplateStatus = (status: string) => {
    return (
      statusTemplate?.statuses.find(candidate =>
        candidate.statusValues?.includes(status) || candidate.id === status
      ) ?? null
    )
  }

  // Get next statuses based on template or use default progression
  const getNextStatuses = (status: TaskStatus): TaskStatus[] => {
    if (statusTemplate?.statuses) {
      // Use template-based progression
      const currentIndex = statusTemplate.statuses.findIndex(candidate =>
        candidate.statusValues?.includes(status) || candidate.id === status
      )
      if (currentIndex !== -1) {
        const nextStatuses: TaskStatus[] = []
        // Add next status in sequence
        if (currentIndex < statusTemplate.statuses.length - 1) {
          const nextStatus = statusTemplate.statuses[currentIndex + 1]
          nextStatuses.push((nextStatus.statusValues?.[0] || nextStatus.id) as TaskStatus)
        }
        // Add previous statuses (allow going back)
        if (currentIndex > 0) {
          const prevStatus = statusTemplate.statuses[currentIndex - 1]
          nextStatuses.push((prevStatus.statusValues?.[0] || prevStatus.id) as TaskStatus)
        }
        return nextStatuses.filter(Boolean)
      }
    }
    
    // Fallback to default progression
    return DEFAULT_STATUS_PROGRESSION[status as BuiltInTaskStatus] || []
  }
  
  const nextStatuses = getNextStatuses(currentStatus)
  const primaryNextStatus = nextStatuses[0] // Most common next status
  
  // Helper to get status label - use template or format custom status
  const getStatusLabel = (status: string): string => {
    const builtInStatus = status as BuiltInTaskStatus

    if (STATUS_LABELS[builtInStatus]) {
      return STATUS_LABELS[builtInStatus]
    }
    // Check status template for custom status
    const templateStatus = getTemplateStatus(status)
    if (templateStatus) {
      return templateStatus.name || status
    }
    // Format custom status nicely (e.g., "contacted_linkedin" -> "Contacted Linkedin")
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }
  
  // Helper to get status color - use template or default
  const getStatusColor = (status: string): string => {
    const builtInStatus = status as BuiltInTaskStatus

    if (STATUS_COLORS[builtInStatus]) {
      return STATUS_COLORS[builtInStatus]
    }
    // Check status template for custom status color
    const templateStatus = getTemplateStatus(status)
    if (templateStatus?.color) {
      return TEMPLATE_COLOR_CLASSES[templateStatus.color] ?? templateStatus.color
    }
    // Default color for custom statuses
    return "bg-blue-100 text-blue-800 border-blue-200"
  }

  const getStatusTemplateMeta = (
    status: string
  ): { title?: string; statuses?: string[] } | null => {
    if (!statusTemplate?.statuses) {
      return null
    }

    const templateStatus = getTemplateStatus(status)

    if (!templateStatus) {
      return null
    }

    return {
      title: templateStatus.name || templateStatus.label,
      statuses: [
        templateStatus.id,
        ...(Array.isArray(templateStatus.statusValues)
          ? templateStatus.statusValues
          : [])
      ].filter(Boolean)
    }
  }

  // Helper to get status icon - use template or default
  const getStatusIcon = (status: string): ReactNode => {
    const normalizedStatus = normalizeStatusKey(status)
    if (normalizedStatus === "cancelled") {
      return (
        <IconX
          size={14}
          data-testid={iconTestIdPrefix ? `${iconTestIdPrefix}-cancelled` : undefined}
        />
      )
    }

    const templateMeta = getStatusTemplateMeta(status)
    const iconName = resolveTaskStatusIconName({
      status,
      title: templateMeta?.title,
      statuses: templateMeta?.statuses
    })

    return (
      <TaskStatusIcon
        iconName={iconName}
        className="text-current"
        testId={iconTestIdPrefix ? `${iconTestIdPrefix}-${iconName}` : undefined}
      />
    )
  }

  const getStatusGroupKey = (status: string): string => {
    const templateStatus = getTemplateStatus(status)
    if (templateStatus?.id) {
      return normalizeStatusKey(templateStatus.id)
    }

    return normalizeStatusKey(status)
  }

  const dropdownStatuses = useMemo(() => {
    const seenGroupKeys = new Set<string>()
    const uniqueStatuses: TaskStatus[] = []
    const statusesForMenu = [currentStatus, ...allStatuses]

    statusesForMenu.forEach((status) => {
      const groupKey = getStatusGroupKey(status)

      if (seenGroupKeys.has(groupKey)) {
        return
      }

      seenGroupKeys.add(groupKey)
      uniqueStatuses.push(status)
    })

    return uniqueStatuses
  }, [allStatuses, currentStatus, statusTemplate])
  
  const handleStatusChange = async (newStatus: TaskStatus) => {
    if (isChanging) return
    
    try {
      setIsChanging(true)
      await onStatusChange(newStatus)
      toast.success(`Status changed to ${getStatusLabel(newStatus)}`)
      // Force a small delay to ensure the UI updates
      setTimeout(() => {
        setIsChanging(false)
      }, 100)
    } catch (error) {
      console.error("Error changing status:", error)
      toast.error("Failed to change status")
      setIsChanging(false)
    }
  }

  const sizeClasses = {
    sm: "h-6 px-2 text-xs",
    md: "h-8 px-3 text-sm", 
    lg: "h-10 px-4 text-base"
  }
  const hasNextAction = showNextAction && Boolean(primaryNextStatus)
  const shouldRenderTestOptions = Boolean(triggerTestId)
  const testOptions = dropdownStatuses.filter(status => status !== currentStatus)

  return (
    <div 
      className={cn("flex min-w-0 max-w-full items-center", className)}
    >
      {/* Clickable Status Badge with Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger 
          asChild
        >
          <Button
            variant="ghost"
            size="sm"
            data-testid={triggerTestId}
            className={cn(
              getStatusColor(currentStatus),
              "h-auto min-w-0 cursor-pointer overflow-hidden border font-medium transition-opacity hover:opacity-80",
              hasNextAction ? "rounded-r-none" : "rounded-full",
              sizeClasses[size],
              isChanging && "opacity-50",
              "px-2 py-0.5"
            )}
            onMouseDown={(e) => {
              // Stop event propagation to prevent parent handlers
              e.stopPropagation()
            }}
            onClick={(e) => {
              // Stop event propagation to prevent parent onClick handlers
              e.stopPropagation()
            }}
          >
            {getStatusIcon(currentStatus)}
            {showLabel && (
              <span className="ml-1 truncate">
                {getStatusLabel(currentStatus)}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
          {shouldRenderTestOptions ? (
            <div hidden>
              {testOptions.map(status => (
                <button
                  key={status}
                  type="button"
                  data-testid={`status-option-${status}`}
                  onClick={() => handleStatusChange(status)}
                >
                  {getStatusLabel(status)}
                </button>
              ))}
            </div>
          ) : null}
          <DropdownMenuContent forceMount align="start" className="w-48">
            {dropdownStatuses.map((status) => (
              <DropdownMenuItem
                key={status}
                data-testid={`status-option-${status}`}
                onClick={() => handleStatusChange(status)}
                disabled={isChanging || status === currentStatus}
                className={cn(
                  "flex items-center gap-2",
                  status === currentStatus && "bg-muted"
                )}
              >
                {getStatusIcon(status)}
                <span>{getStatusLabel(status)}</span>
                {status === currentStatus && (
                  <span className="text-muted-foreground ml-auto text-xs">Current</span>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

      {/* Next Status Arrow Button */}
      {hasNextAction && primaryNextStatus && (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            handleStatusChange(primaryNextStatus)
          }}
          disabled={isChanging}
          className={cn(
            "hover:bg-muted h-6 rounded-l-none border-l-0 px-2 py-0",
            getStatusColor(currentStatus), // Inherit same background color as status badge
            isChanging && "opacity-50"
          )}
          title={`Next: ${getStatusLabel(primaryNextStatus)}`}
          aria-label={`Move task to ${getStatusLabel(primaryNextStatus)}`}
        >
          <IconChevronRight size={14} />
        </Button>
      )}
    </div>
  )
}
